import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Bot, 
  Play, 
  Square, 
  RotateCw, 
  Edit, 
  FileText,
  Check,
  X,
  Clock,
  Cpu,
  Globe,
  AlertTriangle
} from 'lucide-react';
import './Bots.css';

function Bots() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBot, setEditingBot] = useState(null);
  const [newToken, setNewToken] = useState('');
  const [showLogs, setShowLogs] = useState(null);
  const [logs, setLogs] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadBots();
    const interval = setInterval(loadBots, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadBots = async () => {
    try {
      const response = await api.get('/api/admin/bots/status');
      if (response.data.success) {
        // Фильтруем групповой бот
        const filteredBots = response.data.bots.filter(bot => bot.id !== 'tg_group');
        setBots(filteredBots);
      }
    } catch (error) {
      console.error('Ошибка загрузки ботов:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBot = async (botId, enable) => {
    setActionLoading(botId);
    try {
      await api.post(`/api/admin/bots/${botId}/${enable ? 'start' : 'stop'}`);
      await loadBots();
    } catch (error) {
      console.error('Ошибка:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const restartBot = async (botId) => {
    setActionLoading(botId);
    try {
      await api.post(`/api/admin/bots/${botId}/restart`);
      await loadBots();
    } catch (error) {
      console.error('Ошибка перезапуска:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const saveToken = async (botId) => {
    try {
      await api.post(`/api/admin/bots/${botId}/token`, { token: newToken });
      setEditingBot(null);
      setNewToken('');
      await loadBots();
    } catch (error) {
      console.error('Ошибка сохранения токена:', error);
    }
  };

  const loadLogs = async (botId) => {
    try {
      const response = await api.get(`/api/admin/bots/${botId}/logs`);
      if (response.data.success) {
        setLogs(response.data.logs || 'Логи пусты');
      }
    } catch (error) {
      setLogs('Ошибка загрузки логов');
    }
  };

  const getUptime = (lastStarted) => {
    if (!lastStarted) return 'N/A';
    
    const now = new Date();
    const started = new Date(lastStarted);
    const diffMs = now - started;
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}д ${remainingHours}ч`;
    } else if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else {
      return `${minutes}м`;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка ботов...</span>
      </div>
    );
  }

  return (
    <div className="bots-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Управление ботами</h1>
        <button className="btn btn-secondary" onClick={loadBots}>
          <RotateCw size={18} />
          Обновить
        </button>
      </div>

      {bots.length === 0 ? (
        <div className="empty-state">
          <Bot size={64} className="empty-state-icon" />
          <h3 className="empty-state-title">Нет ботов</h3>
          <p className="empty-state-text">Настройте ботов в файле конфигурации</p>
        </div>
      ) : (
        <div className="bots-grid">
          {bots.map(bot => (
            <div key={bot.id} className="bot-card">
              {/* Header */}
              <div className="bot-header">
                <div className="bot-header-left">
                  <div className="bot-avatar">
                    <Bot size={28} />
                  </div>
                  <div className="bot-title">
                    <h3>{bot.name}</h3>
                    <span className="bot-id">ID: {bot.id}</span>
                  </div>
                </div>
                <div className={`bot-status ${bot.status === 'running' ? 'online' : bot.status === 'error' ? 'error' : 'offline'}`}>
                  <span className="status-dot"></span>
                  <span>{bot.status === 'running' ? 'Работает' : bot.status === 'error' ? 'Ошибка' : 'Остановлен'}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="bot-stats">
                <div className="bot-stat">
                  <Globe size={16} />
                  <div className="stat-content">
                    <span className="stat-label">Язык</span>
                    <span className="stat-value">{bot.language || 'N/A'}</span>
                  </div>
                </div>
                <div className="bot-stat">
                  <Clock size={16} />
                  <div className="stat-content">
                    <span className="stat-label">Аптайм</span>
                    <span className="stat-value">{getUptime(bot.last_started)}</span>
                  </div>
                </div>
                <div className="bot-stat">
                  <Cpu size={16} />
                  <div className="stat-content">
                    <span className="stat-label">PID</span>
                    <span className="stat-value">{bot.pid || 'N/A'}</span>
                  </div>
                </div>
                <div className="bot-stat">
                  <Check size={16} />
                  <div className="stat-content">
                    <span className="stat-label">Статус</span>
                    <span className="stat-value">{bot.enabled ? 'Вкл' : 'Выкл'}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="bot-actions">
                {bot.enabled && bot.status === 'running' ? (
                  <button 
                    className="btn btn-danger" 
                    onClick={() => toggleBot(bot.id, false)}
                    disabled={actionLoading === bot.id}
                  >
                    <Square size={16} />
                    Остановить
                  </button>
                ) : (
                  <button 
                    className="btn btn-success" 
                    onClick={() => toggleBot(bot.id, true)}
                    disabled={actionLoading === bot.id}
                  >
                    <Play size={16} />
                    Запустить
                  </button>
                )}
                <button 
                  className="btn btn-warning" 
                  onClick={() => restartBot(bot.id)}
                  disabled={actionLoading === bot.id}
                >
                  <RotateCw size={16} />
                  Рестарт
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setEditingBot(editingBot === bot.id ? null : bot.id);
                    setNewToken(bot.token || '');
                  }}
                >
                  <Edit size={16} />
                  Токен
                </button>
                <button 
                  className="btn btn-ghost"
                  onClick={() => {
                    setShowLogs(showLogs === bot.id ? null : bot.id);
                    if (showLogs !== bot.id) loadLogs(bot.id);
                  }}
                >
                  <FileText size={16} />
                  Логи
                </button>
              </div>

              {/* Edit Token */}
              {editingBot === bot.id && (
                <div className="bot-edit">
                  <input
                    type="text"
                    className="input"
                    value={newToken}
                    onChange={(e) => setNewToken(e.target.value)}
                    placeholder="Введите новый токен"
                  />
                  <div className="edit-actions">
                    <button className="btn btn-success btn-sm" onClick={() => saveToken(bot.id)}>
                      <Check size={16} />
                      Сохранить
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingBot(null)}>
                      <X size={16} />
                      Отмена
                    </button>
                  </div>
                </div>
              )}

              {/* Logs */}
              {showLogs === bot.id && (
                <div className="bot-logs">
                  <pre className="logs-content">{logs}</pre>
                  <div className="logs-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => loadLogs(bot.id)}>
                      <RotateCw size={16} />
                      Обновить
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowLogs(null)}>
                      <X size={16} />
                      Закрыть
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Bots;
